import type { DocumentType } from '@/features/document-templates/types/documentTemplates.types';
import type { BadgeVariant } from '@/shared/components/ui/badge/Badge';
import type { GeneratedDocument, GeneratedDocumentStatus, GenerationFailureReason } from '../types/agreementDocuments.types';

export const PENDING_POLL_WINDOW_MS = 3 * 60 * 1000;

export const DOCUMENT_STATUS_BADGE: Record<GeneratedDocumentStatus, { label: string; variant: BadgeVariant }> = {
    pending: { label: 'Generating', variant: 'info' },
    generated: { label: 'Generated', variant: 'success' },
    failed: { label: 'Failed', variant: 'error' },
};

const SUPPORT_COPY = (documentId: string): string => `Generation failed. Contact support with document reference ${documentId}.`;

const FAILURE_COPY: Record<GenerationFailureReason, (documentId: string) => string> = {
    no_template_assigned: () => "No template is assigned for this document type. An administrator assigns one on the product's Documents tab.",
    agreement_not_found: SUPPORT_COPY,
    agreement_data_unavailable: () => "The agreement's data could not be read. Try again later.",
    render_error: SUPPORT_COPY,
    storage_unavailable: SUPPORT_COPY,
    internal_error: SUPPORT_COPY,
    not_queued: () => 'The request could not be queued. Generate it again.',
};

const FAILURE_DETAIL_COPY: Record<string, string> = {
    no_assignment: 'The product has no template for this document type.',
    agreement_has_no_product: 'The agreement has no product.',
    assigned_template_unavailable: 'The assigned template is deleted or unpublished.',
    document_type_not_assignable: 'This document type cannot be assigned.',
    template_not_renderable: 'The template has no published version.',
    template_owner_mismatch: 'The template belongs to another dealer.',
    invalid_placeholders: 'The template contains invalid placeholders.',
    pdf_conversion_failed: 'The PDF could not be created.',
};

export const failureSummary = (failure: GeneratedDocument['failure'], documentId: string): string =>
    failure ? (FAILURE_COPY[failure.reason] ?? SUPPORT_COPY)(documentId) : '';

export const failureDetail = (failure: GeneratedDocument['failure']): string | null => {
    if (!failure?.detail) {
        return null;
    }
    return FAILURE_DETAIL_COPY[failure.detail] ?? failure.detail;
};

export const triggerLabel = (trigger: string): string => {
    if (trigger === 'on_demand') {
        return 'On demand';
    }
    const words = trigger.replace(/[._]/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const requesterLabel = (requestedBy: GeneratedDocument['requestedBy']): string =>
    requestedBy ? requestedBy.name ?? 'Unknown user' : 'System';

export const templateVersionLabel = (template: GeneratedDocument['template']): string =>
    template ? `${template.name} v${template.versionNumber}` : 'Not resolved';

export const isStillGenerating = (document: GeneratedDocument, now: number): boolean =>
    document.status === 'pending' && now - Date.parse(document.createdAt) >= PENDING_POLL_WINDOW_MS;

export const formatByteSize = (bytes: number | null): string => {
    if (bytes === null) {
        return '';
    }
    return bytes < 1024 ? `${bytes} B` : `${Math.round(bytes / 1024)} KB`;
};

export const shortChecksum = (sha256: string | null): string =>
    sha256 ? `${sha256.slice(0, 8)}…${sha256.slice(-8)}` : '';

/** Agreement reference, document type and UTC date, so a saved file says what it is without opening it. */
export const generatedDocumentFilename = (agreementRef: string, documentType: DocumentType, generatedAt: string): string => {
    const safeRef = agreementRef.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'agreement';

    return `${safeRef}-${documentType.replace(/_/g, '-')}-${generatedAt.slice(0, 10)}.pdf`;
};
